import { useEffect, useState } from "react";
import type { ToolJob } from "./pastedJob";

/** How long a job waits for the tool: past this it would start a tailor nobody pressed for. */
const HANDOFF_TTL_MS = 5 * 60_000;

/**
 * The job pasted before signing in, on its way from the sign-in's return
 * to the tool. Held here in memory and never in history state or storage,
 * and gone at its first read, so a reload or Back finds nothing and sends
 * no second tailor. The tool may never open: a person can move on while
 * its code loads, and the job must not wait for their next visit.
 */
let handed: { job: ToolJob; at: number } | null = null;

/** Pages waiting for a job: the tool can be up before the sign-in's return hands one over. */
const readers = new Set<() => void>();

export function handOverPasted(job: ToolJob): void {
    handed = { job, at: Date.now() };
    for (const read of readers) read();
}

/** The job handed over for this page, once. Null when there is none. */
export function usePastedHandoff(): ToolJob | null {
    const [job, setJob] = useState<ToolJob | null>(null);
    useEffect(() => {
        const read = () => {
            if (!handed) return;
            if (Date.now() - handed.at <= HANDOFF_TTL_MS) setJob(handed.job);
            handed = null;
        };
        read();
        readers.add(read);
        return () => {
            readers.delete(read);
        };
    }, []);
    return job;
}
